import React, { useEffect, useMemo, useState } from "react";
import { FaCalendarCheck, FaInbox } from "react-icons/fa";
import AppShell from "../components/ui/AppShell";
import PageHeader from "../components/ui/PageHeader";

export const AVAILABLE_SLOTS_STORAGE_KEY = "vfs_available_slots_result";

export function saveAvailableSlotsResult(payload) {
  const next = {
    jobId: payload?.jobId ?? null,
    message: payload?.message || "",
    locationLabel: payload?.locationLabel || "",
    slotRows: Array.isArray(payload?.slotRows) ? payload.slotRows : [],
    slots: Array.isArray(payload?.slots) ? payload.slots : [],
    months: Array.isArray(payload?.months) ? payload.months : [],
    originCode: payload?.originCode || "",
    destCode: payload?.destCode || "",
    applicationCentre: payload?.applicationCentre || "",
    appointmentCategory: payload?.appointmentCategory || "",
    subCategory: payload?.subCategory || "",
    scannedAt: payload?.scannedAt || new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(AVAILABLE_SLOTS_STORAGE_KEY, JSON.stringify(next));
  } catch (_error) {
    // Ignore storage failures.
  }

  return next;
}

export function readAvailableSlotsResult() {
  try {
    const raw = sessionStorage.getItem(AVAILABLE_SLOTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function AvailableSlotsPage() {
  const [result, setResult] = useState(() => readAvailableSlotsResult());

  useEffect(() => {
    const sync = () => setResult(readAvailableSlotsResult());
    const onCustom = (event) => {
      if (event?.detail && typeof event.detail === "object") {
        setResult(saveAvailableSlotsResult(event.detail));
        return;
      }
      sync();
    };

    window.addEventListener("storage", sync);
    window.addEventListener("vfs-available-slots-result", onCustom);
    sync();

    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("vfs-available-slots-result", onCustom);
    };
  }, []);

  const slotRows = useMemo(() => {
    if (Array.isArray(result?.slotRows) && result.slotRows.length) {
      return result.slotRows;
    }
    if (Array.isArray(result?.slots) && result.slots.length) {
      return result.slots.map((date) => ({ date, slots: 1 }));
    }
    return [];
  }, [result]);

  const months = Array.isArray(result?.months) ? result.months : [];
  const locationParts = [
    result?.originCode && result?.destCode ? `${result.originCode} → ${result.destCode}` : "",
    result?.applicationCentre,
    result?.appointmentCategory,
    result?.subCategory,
    result?.locationLabel,
  ].filter(Boolean);

  return (
    <AppShell>
      <PageHeader
        title="Available Slots"
        description="Dates found by the extension on VFS (date-availiable + event blocks)."
      />

        {!result ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body p-5 text-center text-muted">
              <FaInbox size={36} className="mb-3 opacity-50" />
              <div className="fw-semibold mb-1">No scan result yet</div>
              <div style={{ fontSize: 14 }}>
                Run a non-Switzerland booking from Booking Details. When the calendar scan finishes,
                you will be redirected here.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body p-4">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 40, height: 40, borderRadius: 10, background: "var(--vp-primary-soft)", color: "var(--vp-primary)" }}
                  >
                    <FaCalendarCheck />
                  </div>
                  <div>
                    <div className="fw-semibold mb-1">
                      {slotRows.length > 0
                        ? `${slotRows.length} available date(s) found`
                        : "No available dates found"}
                    </div>
                    {locationParts.length > 0 && (
                      <div className="text-muted" style={{ fontSize: 13 }}>
                        {locationParts.join(" | ")}
                      </div>
                    )}
                    {result.scannedAt && (
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        Scanned: {new Date(result.scannedAt).toLocaleString()}
                        {result.jobId ? ` · Job #${result.jobId}` : ""}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-lg-7">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h5 className="fw-semibold mb-3">Available</h5>
                    {slotRows.length === 0 ? (
                      <div className="text-muted">No available slots in the scanned months.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Slots (event blocks)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {slotRows.map((row) => (
                              <tr key={row.date}>
                                <td>
                                  <span className="badge text-bg-success">{row.date}</span>
                                </td>
                                <td>{Number(row.slots) || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h5 className="fw-semibold mb-3">Per month</h5>
                    {months.length === 0 ? (
                      <div className="text-muted">No month breakdown stored.</div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {months.map((month) => {
                          const monthRows = Array.isArray(month.slotRows)
                            ? month.slotRows
                            : Array.isArray(month.dates)
                              ? month.dates.map((date) => ({ date, slots: 1 }))
                              : [];
                          const hasAvailable = monthRows.length > 0;
                          return (
                            <div
                              key={`${month.monthIndex}-${month.title}`}
                              className="rounded p-3"
                              style={{
                                background: hasAvailable ? "#f0fdf4" : "#fef2f2",
                                border: `1px solid ${hasAvailable ? "#bbf7d0" : "#fecaca"}`,
                              }}
                            >
                              <div className="fw-semibold mb-1">
                                {month.title || `Month ${month.monthIndex}`}
                              </div>
                              {hasAvailable ? (
                                <div style={{ fontSize: 13 }}>
                                  Available:{" "}
                                  {monthRows.map((r) => `${r.date} (${r.slots})`).join(", ")}
                                </div>
                              ) : (
                                <div className="text-danger" style={{ fontSize: 13 }}>
                                  No available slots this month
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
    </AppShell>
  );
}

export default AvailableSlotsPage;
